import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function MatchSettings() {
  const qc = useQueryClient();
  const { data: probs = [], isLoading } = useQuery({
    queryKey: ["probabilities"],
    queryFn: async () => (await api.get("/match-settings/probabilities")).data,
  });

  const [edits, setEdits] = useState({});

  const save = useMutation({
    mutationFn: async ({ msid, pct }) =>
      api.put("/match-settings/probabilities", {
        match_status_id: msid,
        probability_pct: Number(pct),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["probabilities"] });
      toast.success("Saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="p-8 max-w-3xl">
      <div className="label-eyebrow">Admin</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Match Status Probabilities</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Each match stage maps to a probability % of conversion to Won. Used in the Weighted
        Pipeline Value KPI on the Kanban (sum of Brokerage Amount × this %).
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="label-eyebrow !text-slate-600">Match Status</th>
              <th className="label-eyebrow !text-slate-600 text-right">Probability %</th>
              <th className="label-eyebrow !text-slate-600 text-right">Save</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={3} className="text-center py-6 text-sm text-slate-500">Loading…</td></tr>
            ) : probs.map((p) => {
              const value = edits[p.match_status_id] ?? p.probability_pct;
              const dirty = edits[p.match_status_id] !== undefined && Number(edits[p.match_status_id]) !== p.probability_pct;
              return (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`prob-row-${p.match_status_id}`}>
                  <td className="text-sm font-semibold text-slate-900">{p.match_status_label}</td>
                  <td className="text-right">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={value}
                      onChange={(e) => setEdits({ ...edits, [p.match_status_id]: e.target.value })}
                      className="w-24 inline-block font-mono-num text-right"
                      data-testid={`prob-input-${p.match_status_id}`}
                    />
                  </td>
                  <td className="text-right">
                    <Button
                      size="sm"
                      variant={dirty ? "default" : "ghost"}
                      disabled={!dirty || save.isPending}
                      onClick={() => save.mutate({ msid: p.match_status_id, pct: value })}
                      className={dirty ? "bg-slate-900 hover:bg-slate-800" : ""}
                      data-testid={`prob-save-${p.match_status_id}`}
                    >
                      <Save className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
